import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { API_ENDPOINTS } from '../../api/config';

const Container = styled.div`
  max-height: ${props => props.fullHeight ? '90vh' : '70vh'};
  overflow-y: auto;
  padding: 1rem;

  /* Custom Scrollbar - Theme-aware */
  &::-webkit-scrollbar {
    width: 12px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.scrollbarTrack};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.scrollbarThumb};
    border-radius: 6px;
    border: 2px solid ${props => props.theme.scrollbarTrack};
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${props => props.theme.scrollbarThumbHover};
  }

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme.scrollbarThumb} ${props => props.theme.scrollbarTrack};
`;

const Title = styled.h2`
  color: ${props => props.theme.text};
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
  text-align: center;
  border-bottom: 1px solid ${props => props.theme.border};
  padding-bottom: 1rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.buttonPrimary};
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
`;

const Paragraph = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 0.9375rem;
  line-height: 1.6;
  margin: 0 0 1rem 0;
`;

const List = styled.ul`
  color: ${props => props.theme.textSecondary};
  font-size: 0.9375rem;
  line-height: 1.6;
  margin: 0 0 1rem 0;
  padding-left: 1.5rem;
`;

const ListItem = styled.li`
  margin-bottom: 0.5rem;
`;

const TokenBreakdownList = styled.ul`
  color: ${props => props.theme.textSecondary};
  font-size: 0.9375rem;
  line-height: 1.6;
  margin: 0 0 1rem 0;
  padding-left: 1.5rem;
`;

const TokenBreakdownItem = styled.li`
  margin-bottom: 1.5rem;
  padding: 0.75rem;
  background: ${props => props.theme.cardBackgroundHover};
  border-radius: 6px;
`;

const TokenCategory = styled.div`
  font-weight: 600;
  color: ${props => props.theme.buttonPrimary};
  margin-bottom: 0.5rem;
`;

const TokenRanges = styled.div`
  color: ${props => props.theme.textMuted};
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
`;

const TokenReason = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.8125rem;
  font-style: italic;
`;

const Link = styled.a`
  color: ${props => props.theme.buttonPrimary};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const LastUpdated = styled.div`
  color: ${props => props.theme.textMuted};
  font-size: 0.8125rem;
  text-align: center;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid ${props => props.theme.border};
`;

const PrivacyPolicy = ({ fullHeight = false }) => {
  const [ageRequirement, setAgeRequirement] = useState(13); // Default to 13
  const [locationLoading, setLocationLoading] = useState(true);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.PUBLIC_GEO, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        const country = data.countryCode;

        // EU countries requiring age 16
        const euCountries = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'];
        
        // Countries requiring age 14
        const age14Countries = ['KR', 'CN', 'RU'];
        
        // Countries requiring age 18
        const age18Countries = ['MY', 'ZA', 'IN', 'TR'];

        if (euCountries.includes(country)) {
          setAgeRequirement(16);
        } else if (age14Countries.includes(country)) {
          setAgeRequirement(14);
        } else if (age18Countries.includes(country)) {
          setAgeRequirement(18);
        } else {
          setAgeRequirement(13);
        }
      } catch (error) {
        console.error('Failed to fetch location:', error);
        // Keep default age 13
      } finally {
        setLocationLoading(false);
      }
    };

    fetchLocation();
  }, []);

  return (
    <Container fullHeight={fullHeight}>
      <Title>Privacy Policy</Title>

      <Section>
        <SectionTitle>1. Introduction</SectionTitle>
        <Paragraph>
          PlanMySemester ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our academic planning application.
        </Paragraph>
        <Paragraph>
          By using PlanMySemester, you agree to the collection and use of information in accordance with this policy.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>2. Information We Collect</SectionTitle>

        <Paragraph><strong>2.1 Personal Information</strong></Paragraph>
        <List>
          <ListItem>Name and email address (provided during registration)</ListItem>
          <ListItem>Password (securely hashed using Argon2 encryption)</ListItem>
          <ListItem>Two-factor authentication data (when enabled)</ListItem>
        </List>

        <Paragraph><strong>2.2 Academic Data</strong></Paragraph>
        <List>
          <ListItem>Course information (names, codes, instructors, schedules, grades)</ListItem>
          <ListItem>Assignment details (titles, descriptions, due dates, grades, status)</ListItem>
          <ListItem>Academic preferences and settings</ListItem>
          <ListItem>Calendar and scheduling information</ListItem>
        </List>

        <Paragraph><strong>2.3 Usage Data</strong></Paragraph>
        <List>
          <ListItem>Login and logout times</ListItem>
          <ListItem>Feature usage patterns</ListItem>
          <ListItem>Device and browser information</ListItem>
          <ListItem>IP addresses and location data (for security purposes)</ListItem>
        </List>

        <Paragraph><strong>2.4 Settings and Preferences</strong></Paragraph>
        <List>
          <ListItem>Notification preferences</ListItem>
          <ListItem>Display settings (timezone, date format, theme)</ListItem>
          <ListItem>GPA calculation settings</ListItem>
          <ListItem>Week start preferences</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>2.5 Location Data</SectionTitle>
        <Paragraph>
          <strong>Automatic Location Detection:</strong> When you access our subscription features, we automatically detect your approximate location using your IP address. This information is used solely to:
        </Paragraph>
        <List>
          <ListItem>Provide region-specific pricing and tax calculations</ListItem>
          <ListItem>Display appropriate refund policies based on your location (e.g., EU consumer rights)</ListItem>
          <ListItem>Ensure compliance with local consumer protection laws</ListItem>
        </List>
        <Paragraph>
          <strong>Data Handling:</strong> Location data is not stored permanently and is only used temporarily during your session. We do not create user profiles based on location or use this information for marketing purposes.
        </Paragraph>
        <Paragraph>
          <strong>Legal Basis:</strong> This processing is necessary for the performance of our subscription service contract and to comply with applicable consumer protection regulations.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>3. How We Use Your Information</SectionTitle>
        <Paragraph>We use the collected information for the following purposes:</Paragraph>
        <List>
          <ListItem><strong>Account Management:</strong> To create and manage your user account</ListItem>
          <ListItem><strong>Authentication:</strong> To verify your identity and secure your account</ListItem>
          <ListItem><strong>Academic Planning:</strong> To provide course and assignment management features</ListItem>
          <ListItem><strong>Notifications:</strong> To send assignment reminders and academic alerts</ListItem>
          <ListItem><strong>Analytics:</strong> To calculate grades, completion rates, and academic progress</ListItem>
          <ListItem><strong>Personalization:</strong> To customize your experience based on preferences</ListItem>
          <ListItem><strong>Security:</strong> To protect against unauthorized access and fraud</ListItem>
          <ListItem><strong>Service Improvement:</strong> To analyze usage patterns and improve our features</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>4. Information Sharing and Disclosure</SectionTitle>
        <Paragraph>
          We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following circumstances:
        </Paragraph>
        <List>
          <ListItem><strong>Legal Requirements:</strong> When required by law or to protect our rights</ListItem>
          <ListItem><strong>Service Providers:</strong> With trusted third-party service providers who assist in operating our application (all bound by confidentiality agreements)</ListItem>
          <ListItem><strong>Account Security:</strong> In case of account compromise or security threats</ListItem>
          <ListItem><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</ListItem>
        </List>
        <Paragraph>
          Your academic data remains private and is never shared with other users or third parties without your explicit consent.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>5. Data Security</SectionTitle>
        <Paragraph>
          We implement robust security measures to protect your information:
        </Paragraph>
        <List>
          <ListItem><strong>Password Security:</strong> Passwords are hashed using Argon2 encryption</ListItem>
          <ListItem><strong>Data Encryption at Rest:</strong> All sensitive user data is encrypted in our database using AES-256 encryption with HMAC-SHA-512 signing, including personal information, academic records, AI conversations, and support communications</ListItem>
          <ListItem><strong>Authentication:</strong> JWT tokens and secure cookies for session management</ListItem>
          <ListItem><strong>Two-Factor Authentication:</strong> Optional additional security layer</ListItem>
          <ListItem><strong>Data Encryption:</strong> All data transmitted over HTTPS</ListItem>
          <ListItem><strong>Database Security:</strong> Secure MongoDB with access controls</ListItem>
          <ListItem><strong>Regular Security Audits:</strong> Ongoing monitoring and updates</ListItem>
        </List>
        <Paragraph>
          <strong>Encryption Details:</strong> Sensitive data fields are automatically encrypted before storage and decrypted when accessed by authorized users. This ensures that even if our database were compromised, your personal information would remain protected.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>6. Data Retention</SectionTitle>
        <Paragraph>
          We retain your personal information for as long as your account is active or as needed to provide our services. You can delete your account at any time, which will remove all associated data from our systems.
        </Paragraph>
        <Paragraph>
          Some anonymized usage data may be retained for analytical purposes after account deletion.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>7. Your Rights</SectionTitle>
        <Paragraph>You have the following rights regarding your data:</Paragraph>
        <List>
          <ListItem><strong>Access:</strong> Request a copy of your personal data</ListItem>
          <ListItem><strong>Correction:</strong> Update or correct your information</ListItem>
          <ListItem><strong>Deletion:</strong> Request deletion of your account and data</ListItem>
          <ListItem><strong>Portability:</strong> Export your data in a structured format</ListItem>
          <ListItem><strong>Restriction:</strong> Limit how we process your data</ListItem>
          <ListItem><strong>Objection:</strong> Object to certain data processing activities</ListItem>
        </List>
        <Paragraph>
          To exercise these rights, contact us through the account settings or support channels.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>8. Cookies and Local Storage</SectionTitle>
        <Paragraph>
          We use HTTP-only cookies for authentication and session management. These cookies contain encrypted JWT tokens that are stored server-side and are essential for maintaining secure user sessions.
        </Paragraph>
        <Paragraph>
          User preferences and settings are stored securely in our database and associated with your account. We do not use browser local storage for storing sensitive user data or preferences.
        </Paragraph>
        <Paragraph>
          <strong>Cookie Consent:</strong> By continuing to use PlanMySemester, you consent to the use of these essential cookies. Since these are required for the website to function, there is no option to decline them.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>9. Third-Party Services</SectionTitle>
        <Paragraph>
          PlanMySemester integrates with Stripe solely for secure payment processing. We do not share your personal or academic data with third-party services for marketing, analytics, or any other purposes. All academic data processing and storage occurs exclusively on our secure servers.
        </Paragraph>
        <Paragraph>
          <strong>Payment Processing:</strong> Subscription payments are processed securely through Stripe, a PCI DSS compliant payment processor. When you make a payment:
        </Paragraph>
        <List>
          <ListItem>Your payment information is collected directly by Stripe and never touches our servers</ListItem>
          <ListItem>Stripe handles all payment processing, fraud detection, and compliance</ListItem>
          <ListItem>We receive only confirmation of successful payments and basic transaction details</ListItem>
          <ListItem>Your full payment information is subject to Stripe's privacy policy and security standards</ListItem>
        </List>
        <Paragraph>
          By subscribing to our service, you agree to Stripe's 
          <Link href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </Link> and 
          <Link href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </Link>. 
          Stripe's privacy practices are independent of ours and we encourage you to review them.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>10. Data Processing</SectionTitle>
        <Paragraph>
          PlanMySemester integrates study tools to enhance your academic planning experience. Our system provides study guides, flashcards, syllabus analysis, assignment help, and GPA projections. The specific features available depend on your subscription tier:
        </Paragraph>
        <List>
          <ListItem><strong>Standard (Fast):</strong> Used for GPA calculations, file processing, syllabus uploads, and course uploads</ListItem>
          <ListItem><strong>Enhanced (Reliable):</strong> Used for study guides and tutor assistance</ListItem>
          <ListItem><strong>Advanced (Intelligent):</strong> Used for premium features in Pro and Max plans</ListItem>
        </List>
        <Paragraph>
          <strong>Data Handling:</strong>
        </Paragraph>
        <List>
          <ListItem><strong>Conversations:</strong> Conversations are stored temporarily (7 days) to maintain context and improve responses</ListItem>
          <ListItem><strong>Token Usage:</strong> We track usage for billing and service optimization purposes</ListItem>
          <ListItem><strong>Data Processing:</strong> Your prompts and responses are processed securely</ListItem>
          <ListItem><strong>No Personal Data Sharing:</strong> Academic content is processed anonymously without sharing your personal information</ListItem>
          <ListItem><strong>Academic Integrity:</strong> Responses are designed to promote learning while maintaining academic honesty</ListItem>
        </List>
        <Paragraph>
          <strong>Realistic Token Limits by Action:</strong> Our system uses flexible token limits to provide detailed educational support. The token limits vary by action to balance depth of response with resource efficiency:
        </Paragraph>
        <Paragraph id="token-limits">
          <strong>Token Usage Breakdown:</strong>
        </Paragraph>
        <TokenBreakdownList>

          <TokenBreakdownItem>
            <TokenCategory>Syllabus Analysis (Highest Token Usage)</TokenCategory>
            <TokenRanges>
              • Brief syllabi: 2,000-4,000 tokens<br />
              • Detailed syllabi: 4,000-8,000 tokens<br />
              • Complex multi-component: 8,000-12,000 tokens
            </TokenRanges>
            <TokenReason>5-component framework (Structure, Assessment, Temporal, Resources, Strategy) with deep-dive analysis, strategic planning, and cross-referenced insights.</TokenReason>
          </TokenBreakdownItem>

          <TokenBreakdownItem>
            <TokenCategory>Study Guides (High Token Usage)</TokenCategory>
            <TokenRanges>
              • Simple concepts: 1,000-3,000 tokens<br />
              • Moderate topics: 3,000-6,000 tokens<br />
              • Complex subjects: 6,000-10,000 tokens<br />
              • Advanced synthesis: 8,000-12,000 tokens
            </TokenRanges>
            <TokenReason>Advanced frameworks, learning theory integration, examples, practice questions.</TokenReason>
          </TokenBreakdownItem>

          <TokenBreakdownItem>
            <TokenCategory>Assignment Help (Moderate-High Token Usage)</TokenCategory>
            <TokenRanges>
              • Basic guidance: 1,000-3,000 tokens<br />
              • Intermediate problems: 3,000-6,000 tokens<br />
              • Advanced challenges: 5,000-9,000 tokens
            </TokenRanges>
            <TokenReason>Socratic method, conceptual scaffolding, metacognitive development, transferable skills.</TokenReason>
          </TokenBreakdownItem>

          <TokenBreakdownItem>
            <TokenCategory>Flashcards (Moderate Token Usage)</TokenCategory>
            <TokenRanges>
              • Small sets (5-10): 1,000-2,000 tokens<br />
              • Medium sets (11-25): 2,000-4,000 tokens<br />
              • Large sets (26+): 4,000-7,000 tokens
            </TokenRanges>
            <TokenReason>JSON format is efficient, but detailed answer quality and usage instructions add depth.</TokenReason>
          </TokenBreakdownItem>

          <TokenBreakdownItem>
            <TokenCategory>GPA Projections (Lowest Token Usage)</TokenCategory>
            <TokenRanges>
              • Simple calculations: 500-1,500 tokens<br />
              • Moderate projections: 1,500-3,000 tokens<br />
              • Complex scenarios: 3,000-6,000 tokens
            </TokenRanges>
            <TokenReason>Mathematical calculations are token-efficient, structured analysis frameworks.</TokenReason>
          </TokenBreakdownItem>
        </TokenBreakdownList>
        <Paragraph>
          <strong>Token Usage Hierarchy (Highest to Lowest Average Usage):</strong>
        </Paragraph>
        <List>
          <ListItem>Syllabus Analysis - Most token heavy (8k+ for complex)</ListItem>
          <ListItem>Study Guides - Deep educational content (6k+ for complex)</ListItem>
          <ListItem>Assignment Help - Strategic guidance (4k+ for complex)</ListItem>
          <ListItem>Flashcards - Structured format (3k+ for large sets)</ListItem>
          <ListItem>GPA Projections - Mathematical calculations (2k+ for complex)</ListItem>
        </List>
        <Paragraph>
          <strong>System Design Philosophy:</strong> The token limits reflect the educational value hierarchy - syllabus analysis gets the most tokens because it requires very elaborate strategic planning, study guides get high allocation for pedagogical depth, GPA projections use fewer tokens because calculations are inherently efficient.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>11. Children's Privacy</SectionTitle>
        <Paragraph>
          Our service is designed for academic use and may be used by individuals of various ages. We do not knowingly collect personal information from children under {ageRequirement}. If we become aware that we have collected such information, we will delete it immediately.
        </Paragraph>
        <Paragraph>
          <strong>Regional Age Requirements:</strong> The minimum age for data collection varies by jurisdiction. We automatically determine your location using your IP address to apply the appropriate age requirement ({locationLoading ? 'determining location...' : ageRequirement}) in compliance with local privacy laws.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>12. International Data Transfers</SectionTitle>
        <Paragraph>
          Your data is stored on secure servers. If you are accessing our service from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>13. Changes to This Policy</SectionTitle>
        <Paragraph>
          We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last Updated" date.
        </Paragraph>
        <Paragraph>
          Your continued use of PlanMySemester after any changes constitutes acceptance of the updated policy.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>14. Contact Us</SectionTitle>
        <Paragraph>
          If you have any questions about this Privacy Policy or our data practices, please contact us through the account settings or support features within the application.
        </Paragraph>
        <Paragraph>
          We are committed to addressing your privacy concerns and will respond to inquiries in a timely manner.
        </Paragraph>
      </Section>

      <LastUpdated>
        Last Updated: January 1, 2026
      </LastUpdated>
    </Container>
  );
};

export default PrivacyPolicy;